import locale from "@bonliva-crm/locale";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { ReassignBaAction } from "../components/actions/reassign-ba";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";
import {
  ReassignBaActionFormValues,
  reassignBaFormSchema
} from "../components/actions/action-form-values";
import { useUserProfile } from "@hooks";
import { ConsultantSearchCardDataFragment } from "@graphql";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantReassignBA"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
};

export const useConsultantReassignBaAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { myProfile } = useUserProfile();
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural } = props;

  const consultantReassignBaActionForm = useForm<ReassignBaActionFormValues>({
    defaultValues: {
      responsiblePerson: null,
      resetResponsiblePerson: false
    },
    mode: "onChange",
    resolver: yupResolver(reassignBaFormSchema)
  });

  const { reassignConsultantBa } = useBulkActions({});

  const actionContent = <ReassignBaAction form={consultantReassignBaActionForm} />;

  const getConfirmationData = (): {
    confirmationText: string;
    finalRows: T[];
  } => {
    const genericConfirmationText = locale.bulkActionConfirmation(
      `${locale.ChangeBA} ${locale.On}`,
      totalRows,
      entitySingular,
      entityPlural
    );

    if (myProfile.AzureGroups.includes("crm-group-hba")) {
      const ownEntities = (selectedRows as any).filter(
        (x: ConsultantSearchCardDataFragment) =>
          x.ResponsiblePerson?.PersonId === myProfile._id
      );

      return {
        confirmationText:
          ownEntities.length === totalRows
            ? genericConfirmationText
            : locale.assignBaConfirmation(totalRows, ownEntities.length),
        finalRows: ownEntities
      };
    } else {
      return {
        confirmationText: genericConfirmationText,
        finalRows: selectedRows
      };
    }
  };

  const { confirmationText, finalRows } = getConfirmationData();

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      <Typography variant="body1">{confirmationText}</Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const parsedValues = valueParser(finalRows);

  const onSubmit = consultantReassignBaActionForm.handleSubmit((data) =>
    reassignConsultantBa({
      consultantIds: parsedValues.nodeIds,
      baId: data.responsiblePerson?._id ?? null
    })
  );

  const reset = () => {
    consultantReassignBaActionForm.reset();
  };

  const nextStepDisabled = !consultantReassignBaActionForm.formState.isValid;

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
